package com.zi.support.shared

/**
 * In-app calls to a Support App phone (Locations > Support App device > "Make a call"), voice over LiveKit.
 * functions/lib/device-calls.js starts and updates `deviceCalls/{callId}` and signs the room tokens.
 *
 * [CALL_PAGE] is the call screen on every platform (web iframe, Android WebView, iOS WKWebView). Its bridge:
 *  - host → page: showCall({mode: "outgoing"|"incoming", name}), joinCall(url, token), endCall(reason).
 *  - page → host: {"type": "answer"|"decline"|"hangup"|"close"}, and {"type": "speaker", "on": true|false} for the
 *    loudspeaker button (phones switch the audio route), through webkit.messageHandlers.call (iOS),
 *    CallHost.post (Android) or parent.postMessage({call}) (web). "hangup" carries "reason": "no_answer" when nobody
 *    picked up within [RING_SECONDS].
 * The host also follows the call document and calls endCall when the other side declines or hangs up.
 */
object DeviceCalls {
    const val COLLECTION = "deviceCalls"
    const val RING_SECONDS = 45

    /** Why a call ended, for endCall, from the call document's status. Null while it's still ringing or live. */
    fun endReason(status: String, isCaller: Boolean): String? = when (status) {
        "declined" -> if (isCaller) "Declined" else "Call declined"
        "missed" -> if (isCaller) "No answer" else "Missed call"
        "ended" -> "Call ended"
        else -> null
    }

    // No dollar signs below: Kotlin string.
    const val CALL_PAGE = """<!doctype html>
<html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1,maximum-scale=1">
<title>Call</title>
<script src="https://cdn.jsdelivr.net/npm/livekit-client@2.5.7/dist/livekit-client.umd.js"></script>
<style>
:root{color-scheme:dark;--text:#f5f7fa;--muted:rgba(235,240,245,.62);--green:#30d158;--red:#ff3b30}
*{box-sizing:border-box;-webkit-tap-highlight-color:transparent}
html,body{margin:0;height:100%;color:var(--text);font:15px/1.4 -apple-system,BlinkMacSystemFont,"SF Pro Text",Roboto,"Segoe UI",sans-serif;-webkit-font-smoothing:antialiased}
body{display:flex;flex-direction:column;align-items:center;justify-content:space-between;padding:max(64px,env(safe-area-inset-top)) 28px max(44px,env(safe-area-inset-bottom));text-align:center;
  background:linear-gradient(180deg,#1b2a3a 0%,#121b26 55%,#0b1118 100%)}
.who{display:flex;flex-direction:column;align-items:center}
.avatar{width:96px;height:96px;border-radius:50%;display:grid;place-items:center;font-size:34px;font-weight:600;letter-spacing:.5px;
  background:linear-gradient(160deg,#5b7083,#3c4d5e);box-shadow:0 0 0 0 rgba(255,255,255,.18);margin-bottom:18px}
.ringing .avatar{animation:pulse 2s infinite}
@keyframes pulse{0%{box-shadow:0 0 0 0 rgba(255,255,255,.2)}70%{box-shadow:0 0 0 22px rgba(255,255,255,0)}100%{box-shadow:0 0 0 0 rgba(255,255,255,0)}}
h1{margin:0;font-size:30px;font-weight:600;letter-spacing:-.3px}
#status{margin:6px 0 0;color:var(--muted);font-size:17px;font-variant-numeric:tabular-nums}
#note{margin:10px 0 0;color:#ffd60a;font-size:13px;max-width:300px}
.controls{width:100%;max-width:320px;display:flex;flex-direction:column;align-items:center;gap:30px}
.row{display:flex;justify-content:center;gap:56px;width:100%}
.ctl{display:flex;flex-direction:column;align-items:center;gap:9px;color:var(--text);font-size:13px;font-weight:500}
.ctl button{width:70px;height:70px;border-radius:50%;border:0;padding:0;display:grid;place-items:center;cursor:pointer;color:#fff;
  background:rgba(255,255,255,.14);-webkit-backdrop-filter:blur(20px);backdrop-filter:blur(20px);transition:background .15s,transform .1s}
.ctl button:active{transform:scale(.94)}
.ctl button svg{width:28px;height:28px}
.ctl button.on{background:#fff;color:#111}
.ctl button.green{background:var(--green)}
.ctl button.red{background:var(--red)}
.ctl.big button{width:76px;height:76px}.ctl.big button svg{width:32px;height:32px}
</style></head><body>
<div class="who"><div class="avatar" id="avatar"></div><h1 id="name"></h1><p id="status"></p><p id="note"></p></div>
<div class="controls" id="controls"></div>
<script>
var mode = "outgoing", room = null, ended = false, startedAt = 0, tick = null, ringTimer = null, ring = null;
function post(msg) {
  var m = JSON.stringify(msg);
  if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.call) window.webkit.messageHandlers.call.postMessage(m);
  else if (window.CallHost) window.CallHost.post(m);
  else window.parent.postMessage({call: m}, "*");
}
function status(text) { document.getElementById("status").textContent = text; }
// Line icons, drawn like the phone's own call screen (no emoji).
function icon(paths, fill) {
  return '<svg viewBox="0 0 24 24" fill="' + (fill ? "currentColor" : "none") + '" stroke="' + (fill ? "none" : "currentColor") +
    '" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' + paths + "</svg>";
}
var PHONE = '<path d="M6.6 10.8a15.1 15.1 0 0 0 6.6 6.6l2.2-2.2a1 1 0 0 1 1-.25c1.1.37 2.3.57 3.6.57a1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.25.2 2.45.57 3.57a1 1 0 0 1-.25 1z"/>';
var ICONS = {
  speaker: icon('<path d="M11 5 6 9H3v6h3l5 4z"/><path d="M15.5 8.5a5 5 0 0 1 0 7"/><path d="M18.5 5.5a9.5 9.5 0 0 1 0 13"/>'),
  mic: icon('<rect x="9" y="3" width="6" height="11" rx="3"/><path d="M5.5 11a6.5 6.5 0 0 0 13 0"/><path d="M12 17.5V21"/>'),
  micOff: icon('<rect x="9" y="3" width="6" height="11" rx="3"/><path d="M5.5 11a6.5 6.5 0 0 0 13 0"/><path d="M12 17.5V21"/><path d="M4 4l16 16"/>'),
  answer: icon(PHONE, true),
  hangup: '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" style="transform:rotate(135deg)">' + PHONE + "</svg>",
  close: icon('<path d="M6 6l12 12M18 6 6 18"/>')
};
// [icon, label, class, onTap]: toggles ("" / "on") sit in a row, the red or green call buttons below them.
function controls(list) {
  var box = document.getElementById("controls");
  function ctl(c, i) { return '<div class="ctl' + (/red|green/.test(c[2]) ? " big" : "") + '"><button class="' + c[2] + '" data-i="' + i + '" aria-label="' + c[1] + '">' + c[0] + "</button><span>" + c[1] + "</span></div>"; }
  var toggles = [], calls = [];
  list.forEach(function (c, i) { (/red|green/.test(c[2]) ? calls : toggles).push(ctl(c, i)); });
  box.innerHTML = (toggles.length ? '<div class="row">' + toggles.join("") + "</div>" : "") + (calls.length ? '<div class="row">' + calls.join("") + "</div>" : "");
  box.querySelectorAll("button").forEach(function (b) { b.onclick = function () { list[+b.getAttribute("data-i")][3](b); }; });
}
// A soft two-beep ring (caller) or ring tone (callee) while it's ringing.
function startRing(incoming) {
  try {
    var ctx = new (window.AudioContext || window.webkitAudioContext)();
    function beep() {
      [0, incoming ? 0.25 : 0.45].forEach(function (at) {
        var o = ctx.createOscillator(), g = ctx.createGain();
        o.frequency.value = incoming ? 660 : 440; g.gain.value = 0.08;
        o.connect(g); g.connect(ctx.destination); o.start(ctx.currentTime + at); o.stop(ctx.currentTime + at + 0.35);
      });
    }
    beep(); ring = {ctx: ctx, timer: setInterval(beep, incoming ? 2000 : 3000)};
  } catch (e) {}
}
function stopRing() { if (ring) { clearInterval(ring.timer); ring.ctx.close(); ring = null; } document.body.classList.remove("ringing"); }
var speakerOn = true, muted = false;
function hangupControls() {
  controls([
    [ICONS.speaker, "Speaker", speakerOn ? "on" : "", function (b) {
      speakerOn = !speakerOn; b.classList.toggle("on", speakerOn); post({type: "speaker", on: speakerOn});
    }],
    [ICONS.mic, "Mute", "", function (b) {
      if (!room) return;
      var on = !room.localParticipant.isMicrophoneEnabled;
      muted = !on;
      room.localParticipant.setMicrophoneEnabled(on); b.classList.toggle("on", !on); b.innerHTML = on ? ICONS.mic : ICONS.micOff;
    }],
    [ICONS.hangup, "End", "red", function () { console.warn("call: hung up here"); post({type: "hangup"}); finish("Call ended"); }]
  ]);
}
window.showCall = function (o) {
  mode = o.mode;
  document.getElementById("name").textContent = o.name || "Support App";
  document.getElementById("avatar").textContent = (o.name || "?").trim().split(/\s+/).map(function (w) { return w[0]; }).join("").slice(0, 2).toUpperCase();
  document.body.classList.add("ringing");
  if (mode === "incoming") {
    status("Incoming call…"); startRing(true);
    controls([
      [ICONS.hangup, "Decline", "red", function () { post({type: "decline"}); finish("Call declined"); }],
      [ICONS.answer, "Accept", "green", function () { stopRing(); status("Connecting…"); controls([]); post({type: "answer"}); }]
    ]);
  } else {
    status("Calling…"); startRing(false); hangupControls();
  }
};
function connected() {
  if (startedAt || ended) return;
  stopRing(); clearTimeout(ringTimer); startedAt = Date.now();
  tick = setInterval(function () {
    var s = Math.floor((Date.now() - startedAt) / 1000);
    status(Math.floor(s / 60) + ":" + (s % 60 < 10 ? "0" : "") + (s % 60));
  }, 500);
  hangupControls();
}
window.joinCall = function (url, token) {
  var LK = window.LivekitClient;
  if (!LK) { finish("Couldn't load the call. Check the connection."); post({type: "hangup"}); return; }
  room = new LK.Room();
  var note = function (t) { document.getElementById("note").textContent = t; };
  // A phone that freezes for a moment (screen off, lock screen, weak signal) drops out of the room. Neither side ends the
  // call for that: the other side waits up to 45 s for it to rejoin, and the dropped side rejoins by itself. A real hang-up
  // ends the call through the call record instead (the app calls endCall).
  var waitingForOther = null, rejoins = 0;
  room.on(LK.RoomEvent.ParticipantConnected, function () { clearTimeout(waitingForOther); note(""); connected(); });
  room.on(LK.RoomEvent.ParticipantDisconnected, function (p) {
    console.warn("call: other side dropped", p && p.identity);
    if (!startedAt || ended) return;
    note("Reconnecting…"); clearTimeout(waitingForOther);
    waitingForOther = setTimeout(function () { console.warn("call: other side never came back"); post({type: "hangup", reason: "dropped"}); finish("Call ended"); }, 45000);
  });
  room.on(LK.RoomEvent.TrackSubscribed, function (track) { if (track.kind === "audio") document.body.appendChild(track.attach()); });
  room.on(LK.RoomEvent.Disconnected, function (reason) {
    console.warn("call: disconnected, reason " + reason);
    if (ended) return;
    if (reason === LK.DisconnectReason.CLIENT_INITIATED || rejoins >= 6) { finish("Call ended"); return; }
    rejoins++; note("Reconnecting…");
    setTimeout(function () {
      if (ended) return;
      room.connect(url, token).then(function () {
        rejoins = 0; note("");
        if (room.remoteParticipants.size) { clearTimeout(waitingForOther); connected(); }
        return room.localParticipant.setMicrophoneEnabled(!muted);
      }).catch(function (e) { console.warn("call: rejoin failed", e && e.message); room.disconnect(); });
    }, 1500);
  });
  room.on(LK.RoomEvent.Reconnecting, function () { console.warn("call: reconnecting"); document.getElementById("note").textContent = "Reconnecting…"; });
  room.on(LK.RoomEvent.Reconnected, function () { console.warn("call: reconnected"); document.getElementById("note").textContent = ""; });
  post({type: "speaker", on: speakerOn});
  room.connect(url, token).then(function () {
    if (room.remoteParticipants.size) connected();
    else if (mode === "outgoing") ringTimer = setTimeout(function () {
      if (startedAt || room.remoteParticipants.size) return;  // they answered: never end a live call as unanswered
      console.warn("call: no answer"); post({type: "hangup", reason: "no_answer"}); finish("No answer");
    }, 45000);
    // The microphone is separate: if it fails the call stays up (they can still hear) and says why.
    return room.localParticipant.setMicrophoneEnabled(true).catch(function (e) {
      console.error("call microphone failed", e && e.name, e && e.message);
      document.getElementById("note").textContent = /NotAllowed|permission/i.test(String(e && e.name) + (e && e.message))
        ? "Microphone blocked: allow it for the Support App in the phone's settings."
        : "Microphone problem (" + ((e && (e.name || e.message)) || e) + "). They can't hear you.";
    });
  }).catch(function (e) {
    console.error("call connect failed", e && e.name, e && e.message);
    post({type: "hangup"});
    finish("Couldn't connect the call (" + ((e && e.message) || e) + ")");
  });
};
window.endCall = function (reason) { console.warn("call: ended by app, " + reason); finish(reason || "Call ended"); };
function finish(reason) {
  if (ended) return;
  ended = true; stopRing(); clearTimeout(ringTimer); clearInterval(tick);
  if (room) room.disconnect();
  status(reason);
  controls([[ICONS.close, "Close", "", function () { post({type: "close"}); }]]);
  setTimeout(function () { post({type: "close"}); }, 2500);
}
</script></body></html>"""
}
